import React from 'react';

import {
    setWidgetLoader,
    setGaugeWidgetConfiguration,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import {
    useLazyGetLoadTrendsDataQuery,
    useLazyGetMetricsWidgetDataQuery,
    useLazyGetEnergyFLowAssetsDataQuery,
    useLazyGetConsumptionBreakdownDataQuery,
    useLazyGetAggregatedLineChartDataQuery,
    useLazyGetAggregatedBarChartDataQuery,
    useLazyGetWeatherDataQuery,
    useLazyGetAllSchedulesDataQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { shallowEqual } from 'react-redux';
import { setMetricsData } from '@fiji/common/src/features/widgetManagement/metricsWidgetSlice';
import {
    setLoadTrendData,
    setLoadTrendsPreferences,
} from '@fiji/common/src/features/widgetManagement/loadTrendsWidgetSlice';
import { setBreakdownAssets } from '@fiji/common/src/features/widgetManagement/consumptionBreakdownWidgetSlice';
import { setPreviewWidgetAssetsData } from '@fiji/common/src/features/widgetManagement/energyFlowWidgetSlice';
import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { systemTimezone, timeStampHandler } from '@fiji/common/src/utils/helpers';
import {
    setAggTrendsLatitude,
    setAggTrendsLongitude,
    setAggregatedTrendsData,
    setAggregatedTrendsPreferences,
} from '@fiji/common/src/features/widgetManagement/aggregatedTrendsWidgetSlice';
import {
    setSelectedWeatherAssets,
    setWeatherAssets,
    setWeatherLatitude,
    setWeatherLongitude,
} from '@fiji/common/src/features/widgetManagement/weatherWidgetSlice';
import { setScheduleData } from '@fiji/common/src/features/widgetManagement/scheduleWidgetSlice';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { UserProfile } from '@fiji/common/src/types';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { BemsPayloadHelper } from '@fiji/common/src/utils/bemsPayloadHelper';

type Args = {
    widgetType: string;
};

type ReturnProps = {
    isLoading: boolean;
};

export const useHandleBemsPayload = ({ widgetType }: Args): ReturnProps => {
    const dispatch = useAppDispatch();
    const isInitialRender = React.useRef(true); // Ref to track initial render
    const { assetTypes, assets } = useTypedSelector((state) => state[widgetType], shallowEqual);
    const [previousAssetsLength, setPreviousAssetsLength] = React.useState<number | null>(null);
    const relativeWidgetPayload = useTypedSelector((state) => state[widgetType]);
    const configurationPayload = useTypedSelector((state) => state['widgetConfiguration']);

    const [getEnergyFlowAssetData, { isLoading, isFetching, isError }] = useLazyGetEnergyFLowAssetsDataQuery();
    const [
        getConsumptionBreakdownData,
        { isLoading: consumptionLoader, isFetching: consumptionFetcher, isError: consumptionError },
    ] = useLazyGetConsumptionBreakdownDataQuery();
    const [
        getLoadTrendsData,
        { isLoading: loadTrendsLoader, isFetching: loadTrendsFetcher, isError: loadTrendsError },
    ] = useLazyGetLoadTrendsDataQuery();
    const [getMetricsData, { isLoading: metricsLoader, isFetching: metricsFetcher, isError: metricsError }] =
        useLazyGetMetricsWidgetDataQuery();
    const [getWeatherData, { isLoading: weatherLoader, isFetching: weatherFetcher, isError: weatherError }] =
        useLazyGetWeatherDataQuery();

    const [
        getAggregatedLineChartData,
        { isLoading: aggregatedLineLoader, isFetching: aggregatedLineFetcher, isError: aggregatedLineError },
    ] = useLazyGetAggregatedLineChartDataQuery();
    const [
        getAggregatedBarChartData,
        { isLoading: aggregatedBarLoader, isFetching: aggregatedBarFetcher, isError: aggregatedBarError },
    ] = useLazyGetAggregatedBarChartDataQuery();

    const [getAllSchedulesData, { isLoading: scheduleLoader, isFetching: scheduleFetcher, isError: scheduleError }] =
        useLazyGetAllSchedulesDataQuery();

    const apiCall: { [key: string]: any } = {
        energy_flow: (args: any): any => getEnergyFlowAssetData(args),
        consumption_breakdown: (args: any): any => getConsumptionBreakdownData(args),
        load_trends: (args: any): any => getLoadTrendsData(args),
        metrics: (args: any): any => getMetricsData(args),
        aggregated_trends: (args: any): any =>
            relativeWidgetPayload?.type === 'bar' ? getAggregatedBarChartData(args) : getAggregatedLineChartData(args),
        weather: (args: any): any => getWeatherData(args),
        schedule: (args: any): any => getAllSchedulesData(args),
    };

    const { data: profileDetails } = useGetUserProfileQuery({}) as ApiEndpointType<ApiResponseType<UserProfile>>;

    const [getSelectedGroup, { data: groupsData }]: any = useGetAllGroupsMutation();

    React.useEffect(() => {
        if (groupsData?.data && configurationPayload?.data?.multiDevice) {
            const assetsData = groupsData?.data?.records?.find((item: any) => item?.id === assets?.id) || {}; // Find the record that matches the asset id
            dispatch(setSelectedWeatherAssets(assetsData));
        }
    }, [groupsData?.data]);

    const handleWidgetConfig = (data: any): void => {
        switch (widgetType) {
            case 'energy_flow':
                dispatch(setPreviewWidgetAssetsData(data));
                dispatch(setWidgetLoader(false));
                break;
            case 'consumption_breakdown':
                dispatch(setBreakdownAssets(data));
                dispatch(setWidgetLoader(false));
                break;
            case 'load_trends':
                dispatch(setLoadTrendData(data));
                dispatch(
                    setLoadTrendsPreferences({
                        key: 'timeZone',
                        value: profileDetails?.data?.timezone ?? systemTimezone,
                    })
                );
                dispatch(setWidgetLoader(false));
                break;
            case 'metrics':
                dispatch(setMetricsData(data));
                dispatch(setWidgetLoader(false));
                break;
            case 'aggregated_trends':
                dispatch(setAggregatedTrendsData(data));
                dispatch(
                    setAggregatedTrendsPreferences({
                        key: 'dateFormat',
                        value: profileDetails?.data?.dateFormat ?? 'MM/DD/YYYY',
                    })
                );
                dispatch(
                    setAggregatedTrendsPreferences({
                        key: 'timeZone',
                        value: profileDetails?.data?.timezone ?? systemTimezone,
                    })
                );

                dispatch(setWidgetLoader(false));
                break;
            case 'weather':
                dispatch(setWeatherAssets(data));
                dispatch(setWidgetLoader(false));
                break;
            case 'schedule':
                dispatch(setScheduleData(data));
                dispatch(setWidgetLoader(false));
                break;
            default:
                break;
        }
    };

    const handleWeatherPayload = (): any => {
        const payload: any = {
            unit: profileDetails?.data?.temperatureUnit === 'F°(Fahrenheit)' ? 'F' : 'C',
            timeFilter: relativeWidgetPayload?.selectedTimePeriod?.toUpperCase(),
        };
        if (configurationPayload?.data?.multiDevice) {
            payload['latitude'] = assets?.lat;
            payload['longitude'] = assets?.lng;
            dispatch(setWeatherLatitude(assets?.lat));
            dispatch(setWeatherLongitude(assets?.lng));
        } else {
            payload['latitude'] = configurationPayload?.device?.lat;
            payload['longitude'] = configurationPayload?.device?.lng;
            dispatch(setWeatherLatitude(configurationPayload?.device?.lat));
            dispatch(setWeatherLongitude(configurationPayload?.device?.lng));
        }
        return payload;
    };

    const handleLinkedAPICall = (): boolean => {
        const isMultiDevice = configurationPayload?.data?.multiDevice;

        const getAssetsLength = (assetsList: any): number => {
            if (Array.isArray(assetsList)) {
                return assetsList.length;
            } else if (typeof assetsList === 'object' && assetsList !== null) {
                return Object.keys(assetsList).length;
            }
            return 0; // Default to 0 if assets is neither an array nor an object
        };

        const assetsLength = getAssetsLength(assets);

        if (!isMultiDevice) return true;

        const isInitialAssetSelection = previousAssetsLength === null && assetsLength === 1;
        const isAssetClearedAfterSelection = previousAssetsLength === 1 && assetsLength === 0;
        const hasAssets = assetsLength > 0;

        setPreviousAssetsLength(assetsLength);
        return isInitialAssetSelection || isAssetClearedAfterSelection || hasAssets;
    };

    async function fetchData(): Promise<void> {
        const result: any =
            widgetType !== 'energy_flow' &&
            widgetType !== 'schedule' &&
            (widgetType === 'load_trends'
                ? timeStampHandler(
                      profileDetails?.data?.timezone ?? systemTimezone,
                      profileDetails?.data?.calendarWeek,
                      relativeWidgetPayload?.selectedTimePeriod,
                      null,
                      null,
                      'load_trends'
                  )
                : timeStampHandler(
                      profileDetails?.data?.timezone ?? systemTimezone,
                      profileDetails?.data?.calendarWeek,
                      relativeWidgetPayload?.selectedTimePeriod
                  ));
        const payload: { [key: string]: any } = {
            ...(!configurationPayload?.data?.multiDevice && {
                parentId: configurationPayload?.device?.id,
                parentType:
                    configurationPayload?.device?.deviceProfileId || configurationPayload?.device?.deviceTypeId
                        ? 'GATEWAY'
                        : 'GROUP',
            }),
        };

        // Check if it's the initial render and skip API call if so until assets are empty
        if (isInitialRender.current && widgetType === 'weather') {
            if (assets?.id) getSelectedGroup({ parent: assets?.parentId });
            isInitialRender.current = false;
            return;
        }

        if (widgetType !== 'weather' && widgetType !== 'schedule') payload['widgetTypeId'] = widgetType;

        const weatherPayload: any = widgetType === 'weather' && handleWeatherPayload();
        if (widgetType !== 'weather' && widgetType === 'energy_flow') {
            if (configurationPayload?.data?.multiDevice) {
                payload['assets'] = BemsPayloadHelper.handleLinkedPayloadForEnergyFlow(assets, assetTypes);
            } else {
                payload['assets'] = BemsPayloadHelper.handleRelativePayloadForEnergyFlow(assetTypes);
            }
        } else if (widgetType !== 'weather' && widgetType !== 'energy_flow') {
            if (configurationPayload?.data?.multiDevice) {
                payload['assets'] = BemsPayloadHelper.handleLinkedPayload(assets, assetTypes);
            } else {
                payload['assets'] = BemsPayloadHelper.handleRelativePayload(assetTypes);
            }
        }

        if (widgetType === 'aggregated_trends') {
            payload['unit'] = relativeWidgetPayload?.unitType;
            if (!configurationPayload?.data?.multiDevice) {
                payload['latitude'] = configurationPayload?.device?.lat;
                payload['longitude'] = configurationPayload?.device?.lng;
                dispatch(setAggTrendsLatitude(configurationPayload?.device?.lat));
                dispatch(setAggTrendsLongitude(configurationPayload?.device?.lng));
            }
        }

        if (widgetType === 'metrics' || widgetType === 'load_trends') {
            payload['currentState'] = {
                startTime: result?.from,
                endTime: result?.to,
            };
            payload['previousState'] = {
                startTime: result?.prevFrom,
                endTime: result?.prevTo,
            };
            payload['timeFilter'] = relativeWidgetPayload?.selectedTimePeriod?.toUpperCase();
        } else if (widgetType === 'consumption_breakdown' || widgetType === 'aggregated_trends') {
            payload['startTime'] = result?.from;
            payload['endTime'] = result?.to;
            payload['timeFilter'] = relativeWidgetPayload?.selectedTimePeriod?.toUpperCase();
        }

        const shouldCallApi = handleLinkedAPICall();
        if (!shouldCallApi) {
            return;
        }

        const { error, data }: any = await apiCall[widgetType](widgetType === 'weather' ? weatherPayload : payload);
        if (!error) {
            handleWidgetConfig(data?.data);
        }
    }

    React.useEffect(() => {
        if (relativeWidgetPayload?.secondary) {
            const relativeWidgetPayloadClone = JSON.parse(JSON.stringify(relativeWidgetPayload));
            delete relativeWidgetPayloadClone['multiDevice'];
            dispatch(setGaugeWidgetConfiguration(relativeWidgetPayloadClone));
        }
    }, [relativeWidgetPayload]);

    const hasAnyError =
        isError ||
        consumptionError ||
        loadTrendsError ||
        metricsError ||
        aggregatedBarError ||
        aggregatedLineError ||
        scheduleError;

    React.useEffect(() => {
        if (hasAnyError) {
            dispatch(setWidgetLoader(false));
            if (assets?.length === 0) {
                dispatch(setMetricsData([]));
                dispatch(setLoadTrendData([]));
                dispatch(setBreakdownAssets([]));
                dispatch(setPreviewWidgetAssetsData([]));
                dispatch(setAggregatedTrendsData({}));
            }
        } else if (weatherError) {
            dispatch(setWidgetLoader(false));
            dispatch(setWeatherAssets([]));
        }
    }, [hasAnyError, weatherError]);

    const isAnyLoading =
        isLoading ||
        isFetching ||
        consumptionLoader ||
        metricsFetcher ||
        metricsLoader ||
        consumptionFetcher ||
        loadTrendsFetcher ||
        loadTrendsLoader ||
        weatherLoader ||
        weatherFetcher ||
        aggregatedBarFetcher ||
        aggregatedBarLoader ||
        aggregatedLineFetcher ||
        aggregatedLineLoader ||
        scheduleLoader ||
        scheduleFetcher;

    React.useEffect(() => {
        if (isAnyLoading) {
            dispatch(setWidgetLoader(true));
        }
    }, [isAnyLoading]);

    React.useEffect(() => {
        const isMultiDevice = Boolean(configurationPayload?.data?.multiDevice);
        if (widgetType !== 'weather' && widgetType !== 'aggregated_trends' && Object.keys(assetTypes)?.length) {
            void fetchData();
        }
        if (
            widgetType === 'aggregated_trends' &&
            Object.keys(assetTypes)?.length &&
            (isMultiDevice || // multiDevice case, always call the API don't check for configuration data.
                (!isMultiDevice && configurationPayload?.device)) // For singleDevice, call API after device data is fetched.
        ) {
            void fetchData();
        }
    }, [
        assetTypes,
        assets,
        relativeWidgetPayload?.selectedTimePeriod,
        relativeWidgetPayload?.unitType,
        relativeWidgetPayload?.type,
    ]);

    React.useEffect(() => {
        // Handle multiDevice and singleDevice cases
        const isMultiDevice = configurationPayload?.data?.multiDevice;

        // In multiDevice case, ensure assets are present before API call
        // In singleDevice case, allow the API call even if assets are not present
        if (
            widgetType === 'weather' &&
            configurationPayload?.device &&
            (isMultiDevice ? assets?.id || (isMultiDevice && !isInitialRender?.current) : true)
        ) {
            void fetchData();
        }
    }, [configurationPayload?.device, assets, relativeWidgetPayload?.selectedTimePeriod]);

    return {
        isLoading: isAnyLoading,
    };
};
